import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { HomeService } from '../../../core/home/home.service';
import {
  SponsorSize,
  sponsorHost,
  sponsorSize,
  sponsorUrl,
} from '../../../core/sponsors/sponsor-link';

/** Un logo de la tira, con el rótulo de su grupo si es el primero. */
export interface PartnerItem {
  /** «Auspician», «Colabora»… Sólo en el primer logo del grupo; si no, null. */
  groupLabel: string | null;
  url: string;
  /** Destino, o null si el sponsor no tiene uno configurado. */
  link: string | null;
  /** Dominio del destino, para nombrar el enlace. */
  host: string;
  /** Alto del logo según el peso que le asignó el admin. */
  heightClass: string;
}

/**
 * Alto del logo por peso visual.
 *
 * `medium` es exactamente el alto que tenía la franja antes de ABC-403: sin
 * tamaños asignados —que es el estado de producción hoy— la tira se ve igual
 * que siempre. `small` y `large` se separan lo suficiente para que la jerarquía
 * se lea de un vistazo, sin que un logo chico quede ilegible.
 */
const HEIGHT_BY_SIZE: Record<SponsorSize, string> = {
  small: 'h-20 sm:h-28 lg:h-32',
  medium: 'h-28 sm:h-40 lg:h-48',
  large: 'h-36 sm:h-52 lg:h-64',
};

/** Segundos por logo. Fija la velocidad sin importar cuántos sponsors haya. */
const SECONDS_PER_LOGO = 4;

/**
 * La tira aparece dos veces en el home (arriba y sobre el pie). Con un id fijo,
 * las dos secciones compartían `id="partners-title"` y los dos `aria-labelledby`
 * apuntaban al mismo título — un id duplicado no es HTML válido y la asistencia
 * resuelve siempre el primero.
 */
let instanceCount = 0;

@Component({
  selector: 'app-partners-carousel',
  imports: [NgTemplateOutlet],
  templateUrl: './partners-carousel.component.html',
  styleUrl: './partners-carousel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class PartnersCarouselComponent implements OnInit {
  items: PartnerItem[] = [];

  readonly titleId = `partners-title-${++instanceCount}`;

  constructor(
    private homeService: HomeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // `sponsors$` es un BehaviorSubject que alimenta una sola llamada de home:
    // suscribirse de nuevo acá no agrega tráfico.
    this.homeService.sponsors$.subscribe((sponsors) => {
      this.items = flattenSponsors(sponsors);
      this.cdr.markForCheck();
    });
  }

  /** Vuelta completa. La pista son dos copias de la tira, de ahí el ×2. */
  get duration(): string {
    return `${this.items.length * 2 * SECONDS_PER_LOGO}s`;
  }
}

/**
 * De grupos anidados a una sola tira.
 *
 * El rótulo del grupo viaja pegado a su primer logo en vez de ser un item
 * suelto (ABC-382), y aplanar acá deja el template con un solo `@for` — que es
 * lo que permite repetir la tira para el bucle sin duplicar markup.
 */
export function flattenSponsors(sponsors: any): PartnerItem[] {
  if (!Array.isArray(sponsors)) return [];

  return sponsors.flatMap((section: any) =>
    (section?.imgs ?? []).map((img: any, i: number) => ({
      groupLabel: i === 0 ? (section?.name ?? null) : null,
      url: img?.url,
      link: sponsorUrl(img),
      host: sponsorHost(img),
      heightClass: HEIGHT_BY_SIZE[sponsorSize(img)],
    })),
  );
}
